import cn from "classnames";
import { defaultMultipleSelectProps, ValueContainer } from "components/shared/select";
import { payableStatuses } from "components/distributionBids/paymentFields";
import { accountsReceivableReportSourcesEnum, bidPaymentStatuses, bidPaymentStatusesEnum } from "helpers/enums";
import { paths } from "helpers/paths";
import { reportFilterWrapper, SelectColumnFilter } from "helpers/reactTable";
import { formatValue, typeSpecificProps } from "helpers/reports";
import { alphabeticalSort, enumLabel, ymEvent } from "helpers/system";
import {
    filterSettlementRows,
    settlementLeaves,
    settlementTotals,
    settlementReceived,
    settlementKey,
    settlementLocalFilters,
    settlementCarNumberMatches,
} from "helpers/mutualSettlements";
import isEqual from "lodash/isEqual";
import BidPolicy from "policies/bidPolicy";
import moment from "moment";
import Tooltip from "rc-tooltip";
import React from "react";
import { Link } from "react-router-dom";
import { getItems as cargoOwnersInfo, namesList as counterpartiesList } from "sources/books/cargoOwners";
import { index as distributionBidsList } from "sources/books/distributionBids";
import { listForSelect as hiredCarsListForSelect } from "sources/books/hiredCars";
import { list as legalPersons } from "sources/books/legalPersons";
import { numbersListWithDeleted } from "sources/books/trucks";
import CommonReport from "../commonReport";
import SettlementPanels from "./panels";
import WithChangeDate from "./withChangeDate";
import WithChangeStatus from "./withChangeStatus";
import vatColumn from "./vatColumn";

const NO_PLAN_PAYMENT_DATE_LABEL = "Без даты оплаты";

const isEmptyPlanPaymentDate = (value) =>
    value === null || value === undefined || (typeof value === "string" && value.trim() === "");

const normalizePlanPaymentDate = (row) => ({
    ...row,
    planPaymentDate: isEmptyPlanPaymentDate(row.planPaymentDate) ? null : row.planPaymentDate,
});

const planPaymentDateGroupLabel = (value) => {
    if (isEmptyPlanPaymentDate(value)) {
        return NO_PLAN_PAYMENT_DATE_LABEL;
    }

    const date = moment(value, "YYYY-MM-DD");
    return date.isValid() ? date.format("DD.MM.YYYY") : value;
};

export default class AccountsReceivableReport extends CommonReport {
    state = {
        filters: [],
        expanded: [],
        sortBy: [],
        tableDefaultProps: { columns: [] },
        data: [],
        visibleRows: null,
        visibleRowsSignature: "",
        ...this.props.settlementTableState,
        panel: null,
        panelRevision: 0,
    };

    componentDidMount() {
        this.setReportData(this.props.isPrintForm || this.props.settlementDataReady ? this.props.data.data : []);
    }

    componentDidUpdate(prevProps) {
        if (
            prevProps.demoData !== this.props.demoData ||
            prevProps.source !== this.props.source ||
            prevProps.isAccountsPayable !== this.props.isAccountsPayable ||
            prevProps.groupByDates !== this.props.groupByDates ||
            prevProps.enums?.nDSType !== this.props.enums?.nDSType ||
            prevProps.groupByCounterparties !== this.props.groupByCounterparties
        ) {
            this.setState({ ...this.props.settlementTableState, tableDefaultProps: this.tableDefaultProps() });
        }
        if (prevProps.dataLoadedAt !== this.props.dataLoadedAt || prevProps.data.data !== this.props.data.data) {
            this.setReportData(this.props.data.data);
        }
    }

    setReportData = (data) => {
        const formattedData = this.formatData(data);
        this.setState((state) => ({
            tableDefaultProps: this.tableDefaultProps(),
            data: formattedData,
            reportDataSnapshot: data,
            visibleRows: null,
            visibleRowsSignature: "",
            panel:
                state.panel?.type === "history"
                    ? {
                          ...state.panel,
                          row:
                              formattedData.find((row) => settlementKey(row) === settlementKey(state.panel.row)) ??
                              state.panel.row,
                      }
                    : state.panel,
        }));
        const requestId = (this.reportEnrichmentRequestId || 0) + 1;
        this.reportEnrichmentRequestId = requestId;
        const counterpartyIds = [...new Set(formattedData.map((row) => row.counterpartyId).filter(Boolean))];
        if (counterpartyIds.length > 0) {
            cargoOwnersInfo({ id: { in: counterpartyIds } }).then((counterparties) => {
                if (this.reportEnrichmentRequestId !== requestId || !counterparties) {
                    return;
                }
                const counterpartiesById = counterparties.reduce((result, counterparty) => {
                    result[counterparty.id] = counterparty;
                    return result;
                }, {});
                this.setState((state) => ({
                    data: state.data.map((row) => ({
                        ...row,
                        counterpartyInn: counterpartiesById[row.counterpartyId]?.inn || "",
                    })),
                }));
            });
        }
        const distributionBidIds = [
            ...new Set(
                formattedData
                    .filter((row) => row.isDistributionBid)
                    .map((row) => row.bidId)
                    .filter(Boolean),
            ),
        ];
        if (distributionBidIds.length > 0) {
            distributionBidsList({
                preparedFilter: { id: { in: distributionBidIds } },
                start: 0,
                length: distributionBidIds.length,
            }).then((response) => {
                if (this.reportEnrichmentRequestId !== requestId || !response) {
                    return;
                }
                const orders = response.data || response;
                const ordersById = orders.reduce((result, order) => {
                    result[order.id] = order;
                    return result;
                }, {});
                this.setState((state) => ({
                    data: state.data.map((row) => ({
                        ...row,
                        orderDate: row.isDistributionBid ? ordersById[row.bidId]?.createdAt : undefined,
                    })),
                }));
            });
        }
    };

    componentWillUnmount() {
        this.reportEnrichmentRequestId += 1;
    }

    formatData = (data) =>
        settlementLeaves(data)
            .filter(
                (row) =>
                    !this.props.demoData ||
                    ((!this.props.onlyOverdue || row.isPaymentOverdue) &&
                        (!this.props.previousDayPaymentsOnly || row.previousDayPayment != null)),
            )
            .map((row) => ({
                ...normalizePlanPaymentDate(row),
                received: settlementReceived(row),
                overdueAmount:
                    row.isPaymentOverdue && row.bidPaymentStatus !== bidPaymentStatusesEnum.Paid
                        ? row.remainingAmount
                        : 0,
            }));

    updateData = () => this.refreshPayments();

    refreshPayments = async () => {
        this.setState((state) => ({ panelRevision: state.panelRevision + 1 }));
        await this.props.getItem();
    };

    canEditPayments = () =>
        !this.props.demoData &&
        new BidPolicy({ user: this.props, record: {}, options: {} }).hasTruckingOrganizationWithoutViewersAccess();
    canReadHistory = (row) =>
        !this.props.demoData &&
        (this.canEditPayments() || (!row.isDistributionBid && this.props.roles.isTruckingOrganizationViewer));
    openHistory = (row) => this.setState({ panel: { type: "history", row } });
    closePanel = () => this.setState({ panel: null });

    dataReady = () =>
        (this.props.isPrintForm || this.props.settlementDataReady) &&
        this.state.reportDataSnapshot === this.props.data.data;

    filteredData = () => {
        if (!this.dataReady()) {
            return [];
        }
        const columns = this.state.tableDefaultProps.columns;
        const applyFilters = (rows, filters) =>
            filters.reduce((result, filter) => {
                const column = columns.find((column) => column.accessor === filter.id);
                const filterFunction = column?.filter || this.state.tableDefaultProps.defaultColumn?.filter;
                if (!filterFunction) {
                    return result;
                }
                return filterFunction(
                    result.map((row) => ({ values: row, original: row })),
                    filter.id,
                    filter.value,
                ).map((row) => row.original);
            }, rows);
        return filterSettlementRows(
            this.state.data,
            settlementLocalFilters(this.state.filters, this.props),
            this.props.groupByDates ? "dates" : this.props.groupByCounterparties ? "counterparties" : undefined,
            applyFilters,
        );
    };

    setVisibleRows = (visibleRows) => {
        if (!this.dataReady()) {
            return;
        }
        const visibleRowsSignature = JSON.stringify([this.state.filters, visibleRows]);
        if (visibleRowsSignature !== this.state.visibleRowsSignature) {
            this.setState({ visibleRows, visibleRowsSignature });
            this.props.onSettlementRows?.(visibleRows, this.state.filters, this.state.reportDataSnapshot);
        }
    };

    setTableState = (data) => {
        this.setState(data);
        if (!this.props.isPrintForm) {
            this.props.onSettlementTableState?.(data);
        }
    };

    isHiredCarSource = () =>
        this.props.isAccountsPayable || this.props.source === accountsReceivableReportSourcesEnum.HiredCar;

    isMixedSource = () =>
        !this.props.isAccountsPayable && this.props.source === accountsReceivableReportSourcesEnum.All;

    paymentStatuses = () => (this.props.isAccountsPayable ? payableStatuses : bidPaymentStatuses);

    paymentStatusClassName = (payment) => {
        if (payment.bidPaymentStatus === bidPaymentStatusesEnum.Paid) {
            return "badge-success";
        }
        if (payment.isPaymentOverdue && payment.bidPaymentStatus !== bidPaymentStatusesEnum.Paid) {
            return "badge-danger";
        }
        return "badge-secondary";
    };

    entityLink = (row) => {
        const isDistributionBid = row.original.isDistributionBid;
        const value = row.original.bidId;
        const prefix = this.isMixedSource() ? `${isDistributionBid ? "Заказ" : "Заявка"} № ` : "";
        const path = isDistributionBid ? paths.distributionBidShow(value) : paths.bidShow("bid", value);
        return (
            <Link to={path}>
                {prefix}
                {value}
            </Link>
        );
    };

    extractColumns = () => {
        return [
            {
                Header:
                    this.props.groupByDates || this.props.groupByCounterparties
                        ? "Количество оснований"
                        : this.isMixedSource()
                          ? "Основание"
                          : this.isHiredCarSource()
                            ? "Номер заказа"
                            : "Номер заявки",
                accessor: "bidId",
                aggregate: "count",
                Aggregated: ({ value, row }) => {
                    if (row.isGrouped) {
                        value = row.leafRows.length;
                    }
                    return (
                        <div className="text-center">
                            <b>{formatValue(value, "number")}</b>
                        </div>
                    );
                },
                Cell: ({ value, row }) => (
                    <div className="text-center">{row.isGrouped ? <b>{value}</b> : this.entityLink(row)}</div>
                ),
                ...typeSpecificProps({
                    clrType: this.props.groupByDates || this.props.groupByCounterparties ? "int32" : "string",
                }),
            },
            {
                Header: "Контрагент",
                accessor: "counterpartyName",
                Cell: ({ value, row }) => (
                    <div className="long-text" data-test-id="counterpartyName">
                        {row.isGrouped ? row.subRows[0].original.counterpartyName : value}
                    </div>
                ),
                filter: (rows, _id, filterValue) => {
                    if (!filterValue || !filterValue?.value) {
                        return rows;
                    }
                    const value = filterValue.value;
                    return rows.filter((row) => row.values.counterpartyId === value);
                },
                Filter: reportFilterWrapper((props) => {
                    return (
                        <SelectColumnFilter
                            {...props}
                            onFilteredChange={(payload) => {
                                if (payload.value) {
                                    ymEvent("FilteringСounterpartiesReceivable");
                                }
                                props.onFilteredChange(payload);
                            }}
                            selectProps={{
                                url: counterpartiesList,
                                inputProps: { name: "counterpartyId" },
                                propName: "name",
                            }}
                        />
                    );
                }),
                sortType: (rowA, rowB) => alphabeticalSort(rowA.values, rowB.values, "counterpartyName"),
            },
            {
                Header: "Организация",
                accessor: "legalPersonName",
                Cell: ({ value }) => (
                    <div className="long-text" data-test-id="legalPerson">
                        {value}
                    </div>
                ),
                filter: (rows, _id, filterValue) => {
                    if (!filterValue || !filterValue?.value) {
                        return rows;
                    }
                    const value = filterValue.value;
                    return rows.filter((row) => row.values.legalPersonId === value);
                },
                Filter: reportFilterWrapper((props) => (
                    <SelectColumnFilter
                        {...props}
                        onFilteredChange={(payload) => props.onFilteredChange(payload)}
                        selectProps={{
                            url: legalPersons,
                            inputProps: { name: "legalPersonId" },
                            propName: "name",
                        }}
                    />
                )),
                sortType: (rowA, rowB) => alphabeticalSort(rowA.values, rowB.values, "legalPersonName"),
            },
            {
                Header: "ИНН контрагента",
                accessor: "counterpartyInn",
                Cell: ({ value }) => <div className="text-center">{value}</div>,
                ...typeSpecificProps({ clrType: "string" }),
            },
            { ...vatColumn, options: this.props.enums?.nDSType },
            ...[
                { Header: this.props.isAccountsPayable ? "Сумма к выплате" : "Стоимость", accessor: "price" },
                { Header: this.props.isAccountsPayable ? "Оплачено" : "Получено", accessor: "received" },
                { Header: "Остаток к оплате", accessor: "remainingAmount" },
                { Header: "Просроченная задолженность", accessor: "overdueAmount" },
                ...(this.props.previousDayPaymentsOnly || this.props.groupByCounterparties
                    ? [{ Header: "Поступило за предыдущий день", accessor: "previousDayPayment" }]
                    : []),
            ].map((column) => ({
                ...column,
                aggregate: "sum",
                Aggregated: ({ value }) => (
                    <div className="text-right font-weight-bold">{formatValue(value, "double")}</div>
                ),
                Cell: ({ value }) => <div className="text-right">{formatValue(value, "double")}</div>,
                ...typeSpecificProps({ clrType: "double" }),
            })),
            {
                Header: this.isHiredCarSource() ? "Наёмный ТС" : "ТС",
                accessor: "carNumber",
                Cell: ({ value }) =>
                    value && (
                        <div className="text-center">
                            <span className="carnumber-simple">{value}</span>
                        </div>
                    ),
                ...typeSpecificProps({ clrType: "string" }),
                filter: (rows, _id, filterValue) => {
                    if (!filterValue || !filterValue?.value) {
                        return rows;
                    }
                    if (this.isMixedSource()) {
                        return rows.filter((row) =>
                            settlementCarNumberMatches(row.values.carNumber, filterValue.value),
                        );
                    }
                    return rows.filter((row) => row.values.carId === filterValue.value);
                },
                ...(this.isMixedSource()
                    ? {}
                    : {
                          Filter: reportFilterWrapper((props) => (
                              <SelectColumnFilter
                                  {...props}
                                  selectProps={{
                                      url: this.isHiredCarSource() ? hiredCarsListForSelect : numbersListWithDeleted,
                                      inputProps: { name: "carId" },
                                      propName: "number",
                                  }}
                              />
                          )),
                      }),
            },
            {
                Header: "Маршрут",
                accessor: "route",
                Cell: ({ value }) => <div className="text-wrap-auto two-line-ellipsis">{value}</div>,
                ...typeSpecificProps({ clrType: "string" }),
            },
            ...(this.isHiredCarSource() || this.isMixedSource()
                ? [
                      {
                          Header: "Дата заказа",
                          accessor: "orderDate",
                          Cell: ({ value }) => (
                              <div className="text-center">{value ? moment(value).format("DD.MM.YYYY") : ""}</div>
                          ),
                          ...typeSpecificProps({ clrType: "dateonly" }),
                      },
                  ]
                : []),
            {
                Header: this.props.isAccountsPayable ? "Дата получения счёта" : "Дата выстав. счёта",
                accessor: "invoiceDate",
                Cell: ({ row }) =>
                    !row.isGrouped && (
                        <WithChangeDate
                            name="invoiceDate"
                            payment={row.original}
                            isAccountsPayable={this.props.isAccountsPayable}
                            disabled={!this.canEditPayments()}
                            updateData={(newItem) => this.updateData(newItem, row.index)}
                        />
                    ),
                ...typeSpecificProps({ clrType: "dateonly" }),
            },
            {
                Header: this.props.isAccountsPayable ? "План. дата выплаты" : "План. дата оплаты",
                accessor: "planPaymentDate",
                Cell: ({ value, row }) =>
                    row.isGrouped ? (
                        <div className="text-center">{planPaymentDateGroupLabel(value)}</div>
                    ) : (
                        <WithChangeDate
                            name="planPaymentDate"
                            payment={row.original}
                            isAccountsPayable={this.props.isAccountsPayable}
                            disabled={!this.canEditPayments()}
                            updateData={(newItem) => this.updateData(newItem, row.index)}
                        />
                    ),
                ...typeSpecificProps({ clrType: "dateonly" }),
            },
            {
                Header: this.props.isAccountsPayable ? "Факт. дата выплаты" : "Факт. дата оплаты",
                accessor: "factPaymentDate",
                Cell: ({ row }) =>
                    !row.isGrouped && (
                        <WithChangeDate
                            name="factPaymentDate"
                            payment={row.original}
                            isAccountsPayable={this.props.isAccountsPayable}
                            disabled={!this.canEditPayments()}
                            updateData={(newItem) => this.updateData(newItem, row.index)}
                        />
                    ),
                ...typeSpecificProps({ clrType: "dateonly" }),
            },
            {
                Header: this.props.isAccountsPayable ? "Статус выплаты" : "Статус оплаты",
                accessor: "bidPaymentStatus",
                Cell: ({ value, row: { original, isGrouped, index } }) =>
                    !isGrouped && (
                        <WithChangeStatus
                            payment={original}
                            disabled={!this.canEditPayments()}
                            isAccountsPayable={this.props.isAccountsPayable}
                            updateData={(newItem) => this.updateData(newItem, index)}
                        >
                            <div className="text-center">
                                <span className={cn("badge badge-pill", this.paymentStatusClassName(original))}>
                                    {enumLabel(this.paymentStatuses(), value)}
                                </span>
                            </div>
                        </WithChangeStatus>
                    ),
                filter: (rows, _id, filterValue) => {
                    if (!filterValue || !filterValue?.value) {
                        return rows;
                    }
                    const values = Array.isArray(filterValue.value) ? filterValue.value : [filterValue.value];
                    if (values.length === 0) {
                        return rows;
                    }
                    return rows.filter((row) => values.includes(row.values.bidPaymentStatus));
                },
                Filter: reportFilterWrapper((props) => (
                    <SelectColumnFilter
                        {...props}
                        selectProps={{
                            ...defaultMultipleSelectProps,
                            options: this.paymentStatuses(),
                            inputProps: { name: "bidPaymentStatus" },
                            components: { ValueContainer },
                        }}
                    />
                )),
            },
            {
                Header: "Дней просрочки",
                accessor: "overdueDays",
                Cell: ({ value, row }) => !row.isGrouped && <div className="text-right">{value > 0 ? value : ""}</div>,
                ...typeSpecificProps({ clrType: "int32" }),
            },
            {
                Header: "Комментарий",
                accessor: "comment",
                Cell: ({ value, row }) =>
                    !row.isGrouped && (
                        <Tooltip overlay={value} overlayClassName="tooltip--sm">
                            <div>
                                <WithChangeDate
                                    name="comment"
                                    payment={row.original}
                                    isAccountsPayable={this.props.isAccountsPayable}
                                    disabled={!this.canEditPayments()}
                                    updateData={(newItem) => this.updateData(newItem, row.index)}
                                />
                            </div>
                        </Tooltip>
                    ),
                ...typeSpecificProps({ clrType: "string" }),
            },
            { accessor: "counterpartyId" },
            { accessor: "carId" },
            { accessor: "legalPersonId" },
            { accessor: "bidPaymentStatusReadable" },
        ];
    };

    tableExtraProps = () => ({
        autoResetFilters: false,
        autoResetSortBy: false,
        autoResetExpanded: false,
        autoResetPage: false,
        stateReducer: (next, _action, previous) =>
            !isEqual(next.filters, previous.filters) ? { ...next, pageIndex: 0 } : next,
        manualFilters: true,
        getRowId: (row) => settlementKey(row),
        initialState: {
            hiddenColumns: ["counterpartyId", "carId", "legalPersonId", "bidPaymentStatusReadable"],
            groupBy: this.props.groupByDates
                ? ["planPaymentDate"]
                : this.props.groupByCounterparties
                  ? ["counterpartyName"]
                  : [],
        },
        groupByFn: (rows, columnId) =>
            rows.reduce((groups, row) => {
                const key = String(
                    columnId === "counterpartyName"
                        ? (row.original.counterpartyId ?? "")
                        : (row.values[columnId] ?? ""),
                );
                groups[key] = [...(groups[key] || []), row];
                return groups;
            }, {}),
        data: this.filteredData(),
        onFilteredRowsChange: this.setVisibleRows,
        getRowProps: (row) => ({
            tabIndex: !row.isGrouped && this.canReadHistory(row.original) ? 0 : undefined,
            role: !row.isGrouped && this.canReadHistory(row.original) ? "button" : undefined,
            onKeyDown: (event) => {
                if (
                    event.target === event.currentTarget &&
                    !row.isGrouped &&
                    this.canReadHistory(row.original) &&
                    ["Enter", " "].includes(event.key)
                ) {
                    event.preventDefault();
                    this.openHistory(row.original);
                }
            },
            onClick: (event) => {
                const control = event.target.closest("a, button, input, select, textarea, [role=button]");
                if (
                    !event.currentTarget.contains(event.target) ||
                    row.isGrouped ||
                    !this.canReadHistory(row.original) ||
                    (control && control !== event.currentTarget)
                ) {
                    return;
                }
                this.openHistory(row.original);
            },
            className: row.isGrouped || !this.canReadHistory(row.original) ? "" : "settlements__basis-row",
        }),
    });

    footerAdditionalInfo = () => {
        const totals = settlementTotals(this.filteredData());
        const columns = this.props.isAccountsPayable
            ? [
                  ["Планируется к оплате", "plannedPayments"],
                  ["Оплачено", "paidAmount"],
                  ["Ожидает оплаты", "pendingInvoicedPayments"],
                  ["Просроченная кредиторская задолженность", "overdueDebt"],
              ]
            : [
                  ["Ожидается к оплате", "pendingPayments"],
                  ["Получено", "receivedAmount"],
                  ["Просроченная дебиторская задолженность", "totalDebt"],
              ];
        const counterparty = this.state.filters.find((filter) => filter.id === "counterpartyName")?.value?.option;
        const previous = totals.previousDayPayments;
        return (
            <div className="settlements__totals d-flex flex-wrap align-items-stretch mt-3">
                {columns.map(([label, key]) => (
                    <div className="settlements__total" key={key}>
                        <div className="small text-muted mb-1">{label}</div>
                        <strong>{formatValue(totals[key], "double")}</strong>
                    </div>
                ))}
                <button
                    type="button"
                    aria-pressed={!!this.props.previousDayPaymentsOnly}
                    className={cn("settlements__total settlements__total--interactive text-left", {
                        "settlements__total--active": this.props.previousDayPaymentsOnly,
                    })}
                    onClick={this.props.togglePreviousDay}
                >
                    <div className="small settlements__total-title mb-1">Оплаты предыдущего дня</div>
                    <i className="icon-uEAD5-angle-right settlements__total-chevron" aria-hidden="true" />
                    <strong>{formatValue(previous.totalAmount, "double")}</strong>
                    <div className="small mt-1">
                        Контрагентов: {previous.counterpartiesCount} · Оснований: {previous.bidsCount}
                    </div>
                </button>
                {!this.props.isAccountsPayable &&
                    counterparty &&
                    (this.canEditPayments() || this.props.roles.isTruckingOrganizationViewer) &&
                    !this.props.demoData && (
                        <button
                            type="button"
                            className="settlements__total settlements__total--interactive text-left"
                            aria-haspopup="dialog"
                            onClick={() => this.setState({ panel: { type: "discipline", counterparty } })}
                        >
                            <div className="small text-muted mb-1">{counterparty.name}</div>
                            <span className="font-weight-bold settlements__total-title">Платёжная дисциплина</span>
                            <i className="icon-uEAD5-angle-right settlements__total-chevron" aria-hidden="true" />
                        </button>
                    )}
            </div>
        );
    };

    underTableData = () => (
        <>
            {this.dataReady() ? (
                this.footerAdditionalInfo()
            ) : (
                <div className="alert alert-light mt-3 mb-0" role="status">
                    {this.props.settlementPending
                        ? "Обновляем строки и итоги отчёта…"
                        : "Строки и итоги недоступны для выбранных фильтров. Обновите отчёт."}
                </div>
            )}
            {!this.props.isPrintForm && (
                <SettlementPanels
                    panel={this.state.panel}
                    close={this.closePanel}
                    openHistory={this.openHistory}
                    openUpcoming={() => this.setState({ panel: { type: "upcoming" } })}
                    revision={this.state.panelRevision}
                    refresh={this.refreshPayments}
                    filters={this.state.filters}
                    canEdit={this.canEditPayments()}
                    {...this.props}
                />
            )}
        </>
    );
}
